import React, { useMemo } from 'react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { formatMacro } from '@/shared/utils/formatting';
import { motion, AnimatePresence } from 'framer-motion';
import { PILLARS, ALL_PILLAR_KEYS, NUTRIENT_REGISTRY, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';

interface DiagnosticCardProps {
    data: any; // Accept item.micros
    isVisible: boolean;
    themeColor: string;
}

/**
 * DiagnosticCard: Inline expandable nutrient breakdown.
 * V8.4: FIXED. Correctly normalizes keys to prevent duplication in 'Other'.
 */
export const DiagnosticCard: React.FC<DiagnosticCardProps> = ({ data, isVisible, themeColor }) => {
    
    const resolveValue = (key: string) => {
        const val = data[key];
        if (val === undefined || val === null) return 0;
        if (typeof val === 'object' && 'total' in val) return val.total;
        if (typeof val === 'object' && 'amount' in val) return val.amount;
        if (typeof val === 'string') {
            const numericPart = val.replace(/[^0-9.]/g, '');
            return parseFloat(numericPart) || 0;
        }
        return Number(val) || 0;
    };

    const resolveUnit = (key: string) => {
        const val = data[key];
        if (val && typeof val === 'object' && val.unit) return val.unit;
        const lowKey = normalizeNutrientKey(key);
        return NUTRIENT_REGISTRY[lowKey]?.unit || 'mg';
    };

    const hasAnyPillarData = PILLARS.some(p => p.keys.some(k => resolveValue(k) > 0));
    
    // Identify non-pillar nutrients (Hardened normalization)
    const otherCompounds = useMemo(() => {
        const blacklist = [
            ...ALL_PILLAR_KEYS, 
            'discovered_micros', 'detailed_metrics', 'items', 'quantity', 'item_name', 'id', 'type', 
            'calories', 'protein', 'carbs', 'fat', 'insulin_load', 'glycemic_index', 'food_insulin_index', 
            'insulin_spike_risk', 'dosage', 'compound_name', 'timing_window', 'items_checked'
        ];
        return Object.keys(data)
            .filter(k => {
                const canonicalKey = normalizeNutrientKey(k);
                return !blacklist.includes(canonicalKey) && resolveValue(k) > 0;
            })
            .map(k => ({
                id: k,
                name: NUTRIENT_REGISTRY[normalizeNutrientKey(k)]?.label || k.replace(/_/g, ' '),
                amount: resolveValue(k),
                unit: resolveUnit(k)
            }));
    }, [data]);

    const hasAnyData = hasAnyPillarData || otherCompounds.length > 0;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    style={{ overflow: 'hidden' }}
                >
                    <Box style={{ 
                        marginTop: 10, 
                        padding: 18, 
                        backgroundColor: 'rgba(0,0,0,0.4)', 
                        borderRadius: 14, 
                        borderWidth: 1, 
                        borderColor: 'rgba(255,255,255,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20
                    }}>
                        {!hasAnyData ? (
                            <Typography style={{ fontSize: 10, color: '#444', textAlign: 'center', padding: 10, fontStyle: 'italic' }}>
                                NO MICRONUTRIENT DATA RECORDED
                            </Typography>
                        ) : (
                            <>
                                {/* 1. THE 4 PILLARS GRID */}
                                {hasAnyPillarData && (
                                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 } as any}>
                                        {PILLARS.map(pillar => {
                                            const activeInPillar = pillar.keys.filter(k => resolveValue(k) > 0);
                                            if (activeInPillar.length === 0) return null;

                                            return (
                                                <Box key={pillar.id}>
                                                    <Typography style={{ fontSize: 10, fontWeight: '950', color: pillar.color, letterSpacing: 1.5, marginBottom: 10, textTransform: 'uppercase' }}>
                                                        {pillar.label}
                                                    </Typography>
                                                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {activeInPillar.map(key => {
                                                            const profile = NUTRIENT_REGISTRY[normalizeNutrientKey(key)];
                                                            const label = profile?.label || key.replace('_', ' ');
                                                            const subLabel = profile?.subLabel;
                                                            return (
                                                                <Box key={key} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                                    <Box>
                                                                        <Typography style={{ fontSize: 11, color: '#666', fontWeight: '800', textTransform: 'uppercase' }}>
                                                                            {label}
                                                                        </Typography>
                                                                        {subLabel && (
                                                                            <Typography style={{ fontSize: 8, color: '#444', fontWeight: '700', textTransform: 'uppercase' }}>
                                                                                {subLabel}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                    <Typography style={{ fontSize: 16, fontWeight: '950', color: '#fff', fontVariantNumeric: 'lining-nums' } as any}>
                                                                        {formatMacro(resolveValue(key))}
                                                                        <Typography style={{ fontSize: 9, color: '#444', fontWeight: '900' }}> {resolveUnit(key)}</Typography>
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                {/* 2. OTHER ACTIVE COMPOUNDS (Cyan Section) */}
                                {otherCompounds.length > 0 && (
                                    <Box style={{ 
                                        borderTopWidth: hasAnyPillarData ? 1 : 0, 
                                        borderTopColor: 'rgba(255,255,255,0.08)', 
                                        paddingTop: hasAnyPillarData ? 20 : 0 
                                    }}>
                                        <Typography style={{ fontSize: 10, fontWeight: '950', color: '#06b6d4', letterSpacing: 1.5, marginBottom: 12, textTransform: 'uppercase' }}>
                                            OTHER ACTIVE COMPOUNDS
                                        </Typography>
                                        <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 } as any}>
                                            {otherCompounds.map((oc, i) => (
                                                <Box key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                                    <Typography style={{ fontSize: 11, color: '#666', fontWeight: '800', textTransform: 'uppercase', flex: 1, paddingRight: 8 }}>
                                                        {oc.name}
                                                    </Typography>
                                                    <Typography style={{ fontSize: 16, fontWeight: '950', color: '#06b6d4', fontVariantNumeric: 'lining-nums' } as any}>
                                                        {formatMacro(oc.amount)}<Typography style={{ fontSize: 9, color: '#444', fontWeight: '900' }}> {oc.unit}</Typography>
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
